import exportPdf from './exportPdf';
import exportExcel from './exportExcel';
import exportCsv from './exportCsv';
import useBrandReportHeader from '../hooks/useBrandReportHeader';

const safeText = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

/**
 * Núcleo central para exportação unificada de relatórios CRUD.
 * Coordena formatação, cabeçalhos e integração com PDF, Excel e CSV.
 */
const useExportReportBase = ({
  title = 'Relatório',
  columns = [],
  rows = [],
  theme,
  t,
  options = {},
}) => {
  const { logoUrl, title: headerTitle, subtitle, footer } = useBrandReportHeader({
    titleKey: options.titleKey,
    fallbackTitle: options.headerTitle || title,
  });
  const generatedAt = new Date();
  const generatedOnText = t
    ? t('reportGeneratedOn', { datetime: generatedAt.toLocaleString() })
    : `Generated on: ${generatedAt.toLocaleString()}`;

  const safeColumns = (columns || []).filter((c) => c.field && c.headerName);
  const safeRows = Array.isArray(rows) ? rows : [];

  const buildData = () =>
    safeRows.map((r) => {
      const mapped = {};
      safeColumns.forEach((col) => {
        const key = col.headerName || col.field;
        const val =
          typeof col.valueGetter === 'function'
            ? col.valueGetter({ row: r })
            : r[col.field];
        mapped[key] = safeText(val);
      });
      return mapped;
    });

  const noDataMessage = t ? t('reportNoData') : 'No data available'; // t(...) fornece mensagem localizada quando não há dados

  const includeWatermark = options.includeWatermark !== false;

  const sharedBranding = {
    header: {
      logoUrl,
      title: safeText(headerTitle || title),
      subtitle: safeText(subtitle),
      generatedOn: safeText(generatedOnText),
    },
    footer: safeText(footer),
    watermark: includeWatermark && logoUrl
      ? {
          image: logoUrl,
          opacity: 0.05,
        }
      : null,
  };

  const handleExportCsv = async () => {
    const data = buildData();
    if (!data.length) {
      console.warn(noDataMessage);
      return;
    }
    await exportCsv({
      title,
      data,
      t,
      branding: sharedBranding,
    });
  };

  const handleExportXlsx = async () => {
    const data = buildData();
    if (!data.length) {
      console.warn(noDataMessage);
      return;
    }
    const map = new Map([[title, data]]);
    await exportExcel({
      title,
      filename: `${title}.xlsx`,
      sheetsMap: map,
      theme,
      t,
      branding: sharedBranding,
    });
  };

  const handleExportPdf = async () => {
    const data = buildData();
    if (!data.length) {
      console.warn(noDataMessage);
      return;
    }
    await exportPdf({
      title,
      rows: data,
      columns: safeColumns,
      theme,
      t,
      branding: sharedBranding,
    });
  };

  const handleExport = (type) => {
    switch (type) {
      case 'csv':
        return handleExportCsv();
      case 'excel':
        return handleExportXlsx();
      case 'pdf':
        return handleExportPdf();
      default:
        console.error('Tipo inválido de exportação:', type);
    }
  };

  return { handleExportCsv, handleExportXlsx, handleExportPdf, handleExport };
};

export default useExportReportBase;
